/**
 * Site UI / URL language codes only (header language switcher, i18n routes).
 * Quran translation-only languages live in `quranTranslationLanguages.ts`.
 */

export const SUPPORTED_LANG_CODES = ['ar', 'en', 'fr', 'tr', 'ur', 'ms'] as const
export type LangCode = (typeof SUPPORTED_LANG_CODES)[number]

export const DEFAULT_LANG: LangCode = 'ar'

/** Display names and direction for the site language switcher (client-side). */
export const LANGUAGE_UI_METADATA: Record<
  LangCode,
  { name: string; nativeName: string; direction: 'ltr' | 'rtl' }
> = {
  ar: { name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  en: { name: 'English', nativeName: 'English', direction: 'ltr' },
  fr: { name: 'French', nativeName: 'Français', direction: 'ltr' },
  tr: { name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
  ur: { name: 'Urdu', nativeName: 'اُردُو', direction: 'rtl' },
  ms: { name: 'Malay', nativeName: 'Bahasa Melayu', direction: 'ltr' },
}

export type ClientUiLanguage = {
  code: LangCode
  name: string
  nativeName: string
  direction: 'ltr' | 'rtl'
  isDefault: boolean
}

/** All languages offered in the public UI (order = SUPPORTED_LANG_CODES). */
export function getClientUiLanguages(): ClientUiLanguage[] {
  return SUPPORTED_LANG_CODES.map((code) => ({
    code,
    ...LANGUAGE_UI_METADATA[code],
    isDefault: code === DEFAULT_LANG,
  }))
}

export function directionForLangCode(code: string): 'ltr' | 'rtl' {
  const base = code.split('-')[0].toLowerCase() as LangCode
  if (SUPPORTED_LANG_CODES.includes(base)) return LANGUAGE_UI_METADATA[base].direction
  return base === 'ar' || base === 'ur' ? 'rtl' : 'ltr'
}

export function isSupportedLang(code: string): code is LangCode {
  return SUPPORTED_LANG_CODES.includes(code.toLowerCase() as LangCode)
}

/**
 * Get language code from pathname. Path is expected as /:lang/... or /:lang
 * Base path (e.g. /sakeena) is not stripped here; pathname should be as from useLocation().
 */
export function getLangFromPathname(pathname: string): LangCode {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]?.toLowerCase()
  if (first && isSupportedLang(first)) return first as LangCode
  return DEFAULT_LANG
}

/**
 * Build path with language prefix: /ar/about, /en/library
 */
export function localizePath(lang: string, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  const langPrefix = lang ? `/${lang}` : `/${DEFAULT_LANG}`
  if (p === '/' || p === '') return langPrefix
  return `${langPrefix}${p}`
}

/**
 * Strip language segment from pathname to get "rest" path.
 * /ar/library -> /library, /en -> /
 */
export function pathWithoutLang(pathname: string, lang: string): string {
  const prefix = `/${lang}`
  if (pathname === prefix || pathname.startsWith(prefix + '/')) {
    const rest = pathname.slice(prefix.length) || '/'
    return rest
  }
  return pathname
}
